/**
 * QMP-FE-8d — THE RULE ROWS OF STEPS 2–4 (mockup v83
 * `docs/design/mockups/mockup-qmp-v83-official.html`: `summary` l. 1762–1782,
 * `countLine` l. 1783–1794, `row` l. 1795–1802, `footer` l. 1803–1805,
 * `routeText` / `noticeText` / `handoverPage` l. 1831–1859). PURE: no React,
 * no api.
 *
 * Every sentence is built from what the server serves: the editor's state of
 * the subarea (`{general, rows}`, the grammar of `lib/qmpRules` — `follow`,
 * `own`, `auto`) and the LABELS of the served choices (`standard_options`). A
 * value outside the served choices reads through the formatters of
 * `lib/qmpRules`. Nothing here decides a rule.
 *
 * The grammar of the count line (v83 `own` / `isStandard` / `isNa`):
 *   · `follow`                    → counted in "Common rule · n QWPs";
 *   · `own`                       → "k with a rule of its own (<names>)";
 *   · `auto` in a package where the subarea exists
 *                                 → "standard rule adapted to the parties of <names>";
 *   · `auto` where it does not (`forcedNull`)
 *                                 → "not applicable in <names>".
 *
 * Owner's decisions (2026-11-04): a party nobody is appointed to reads "Not
 * assigned" (v83 "To be appointed"); the handover UNIT is never said — no
 * "Together / One by one", no unit value (the v6 handover lots are not
 * approved yet).
 */
import {
  fieldOptions, sameValue, samePreparedBy, forcedNull, valueOf, receiverLimitOf,
  PATH_POSITIONS, POSITION_LABEL,
  formatDuration, formatNotice, formatSignoffDue, formatResponseTime, formatHandoverNotice,
} from './qmpRules';

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const lc = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '');
const joinNames = (rows) => rows.map((r) => r.name).join(', ');

/** The label the server serves for a value of a field, else the fallback. */
export function servedLabel(options, section, field, value, fallback, same = sameValue) {
  const hit = fieldOptions(options, section, field).find((o) => same(o.value, value));
  if (hit) return hit.label;
  return fallback ? fallback(value) : '';
}

// ---------------------------------------------------------------------------
// the summary sentence of a rule (v83 `summary`)
// ---------------------------------------------------------------------------

const refLabel = (ref, options, names) => {
  if (ref && ref.position) {
    return servedLabel(options, 'itp_rules', 'endorsement.endorsed_by[]', ref, () => POSITION_LABEL[ref.position]);
  }
  return (ref && names && names[ref.company_id]) || '—';
};

/** v83 `endorsers`: "Verification and Acceptance Parties"; a list that names a
 *  company is joined as it reads. */
export function endorsers(labels) {
  if (!labels.length) return '';
  const parties = labels.every((l) => / Party$/.test(l));
  const short = parties ? labels.map((l) => l.replace(/ Party$/, '')) : labels;
  const joined = short.length === 1 ? short[0]
    : `${short.slice(0, -1).join(', ')} and ${short[short.length - 1]}`;
  if (!parties) return joined;
  return `${joined}${labels.length > 1 ? ' Parties' : ' Party'}`;
}

const holdDue = (label) => (label === 'End of working day'
  ? 'end of the working day of the inspection' : `${lc(label)} the booked inspection`);
const witnessDue = (label) => (label === 'End of working day'
  ? 'end of the working day the window closes' : `${lc(label)} the window closes`);

/** v83 `noticeWords`: "2 working days before" → "2 working days’ notice". */
export function noticeWords(label) {
  const d = String(label || '').replace(/ before$/, '');
  return /^1 (working day|hour)$/.test(d) ? `${d}’s notice` : `${d}’ notice`;
}

const punchWords = (h) => ((h && h.qa_complete_with_open_items)
  ? `QA Complete allowed with Category ${(h.open_categories || []).join(' and ')} items open`
  : 'QA Complete needs every punch list item closed');

/** v83 `punchShort` — the QA Complete line of the punch list card. */
export const punchShort = (h) => ((h && h.qa_complete_with_open_items)
  ? `Category ${(h.open_categories || []).join(' and ')} may stay open`
  : 'Every punch list item closed');

const dueLabel = (options, section, field, d) => servedLabel(options, section, field, d, formatResponseTime);
const handoverNoticeLabel = (options, d) => servedLabel(options, 'handover', 'handovers[].notice', d, formatHandoverNotice);

/** The sentence of a subarea's rule. `ctx.options` = the served choices,
 *  `ctx.names` = `{company_id: name}` for a rule that names a company. */
export function ruleSummary(section, value, ctx = {}) {
  const { options, names } = ctx;
  if (value == null) return 'Not applicable';
  const v = value;
  if (section === 'itp_rules') {
    const prepared = servedLabel(options, 'itp_rules', 'prepared_by', v.prepared_by || [],
      (refs) => (refs || []).map((r) => refLabel(r, options, names)).join(' or '), samePreparedBy);
    const e = v.endorsement || {};
    if (!e.required) return `${prepared} prepares · No endorsement required`;
    const list = (e.endorsed_by || []).map((r) => refLabel(r, options, names));
    const response = servedLabel(options, 'itp_rules', 'endorsement.response_time', e.response_time ?? null, formatResponseTime);
    const overdue = servedLabel(options, 'itp_rules', 'endorsement.if_overdue', e.if_overdue,
      (x) => (x === 'proceed_at_risk' ? 'Proceed at risk' : 'Wait for endorsement'));
    return `${prepared} prepares · ${endorsers(list)} endorse${list.length === 1 ? 's' : ''}`
      + ` · Response time: ${lc(response)} · If overdue: ${lc(overdue)}`;
  }
  if (section === 'hold_points') {
    const notice = servedLabel(options, 'hold_points', 'notice', v.notice ?? null, formatNotice);
    const signoff = servedLabel(options, 'hold_points', 'signoff_due', v.signoff_due, formatSignoffDue);
    const doc = v.document_release_due == null ? null
      : servedLabel(options, 'hold_points', 'document_release_due', v.document_release_due, formatDuration);
    return `Booking notice: ${lc(notice)} · Sign-off due (inspection): ${holdDue(signoff)}`
      + ` · Release due (document): ${doc == null ? 'set in each ITP' : `${doc} after the evidence`}`;
  }
  if (section === 'witness_points') {
    const signoff = servedLabel(options, 'witness_points', 'signoff_due', v.signoff_due, formatSignoffDue);
    const notice = v.notice == null ? 'no minimum notice'
      : `${servedLabel(options, 'witness_points', 'notice', v.notice, formatNotice)} the window opens`;
    return `Notice: ${notice} · Sign-off due: ${witnessDue(signoff)}`;
  }
  if (section === 'ncr') {
    if (v.per_company) return 'Response and decision due vary between companies';
    const r = dueLabel(options, 'ncr', 'response_due', (v.response_due || {}).all ?? null);
    const d = dueLabel(options, 'ncr', 'decision_due', (v.decision_due || {}).all ?? null);
    return `Response due: ${lc(r)} · Decision due: ${lc(d)} · same for all companies`;
  }
  if (section === 'rfi') {
    if (v.per_company) return 'Answer due varies between companies';
    const a = dueLabel(options, 'rfi', 'answer_due', (v.answer_due || {}).all ?? null);
    return `Answer due: ${lc(a)} · same for all companies`;
  }
  if (section === 'visibility') {
    const ap = [v.acceptance_sees_ev_ncrs ? 'NCRs' : '', v.acceptance_sees_ev_rfis ? 'RFIs' : ''].filter(Boolean);
    const ep = [v.executing_sees_va_ncrs ? 'NCRs' : '', v.executing_sees_va_rfis ? 'RFIs' : ''].filter(Boolean);
    const parts = [];
    if (ap.length) parts.push(`Acceptance Party also sees Executing–Verification ${ap.join(' and ')}`);
    if (ep.length) parts.push(`Executing Party also sees Verification–Acceptance ${ep.join(' and ')}`);
    return parts.length ? parts.join(' · ') : 'Only the companies involved in each NCR or RFI can see it';
  }
  if (section === 'ai') {
    return v.allowed ? 'AI may help prepare drafts — a person reviews, adopts and signs'
      : 'AI-assisted drafting not allowed';
  }
  if (section === 'handover') {
    const path = v.path || 'none';
    if (path === 'none') return 'No handover — internal QA';
    const pathLabel = servedLabel(options, 'handover', 'path', path, (p) => p);
    const h = v.handovers || [];
    const notices = path === 'EVA'
      ? `${noticeWords(handoverNoticeLabel(options, h[0] && h[0].notice))} (handover 1) · `
        + `${noticeWords(handoverNoticeLabel(options, h[1] && h[1].notice))} (handover 2)`
      : noticeWords(handoverNoticeLabel(options, h[0] && h[0].notice));
    const limit = receiverLimitOf(v);
    const limitText = limit.kind === 'days' && limit.value != null
      ? `Receiving company signs the handover within ${formatDuration({ value: limit.value, unit: 'working_days' })}`
      : 'No time limit for the receiving company to sign the handover';
    return `${pathLabel} · ${notices} · ${limitText}`;
  }
  if (section === 'punch_list') {
    const h = v.handovers || [];
    return `Category A always blocks handover and QA Complete · ${v.per_handover
      ? `Handover 1: ${punchWords(h[0])} · Handover 2: ${punchWords(h[1])}`
      : punchWords(h[0])}`;
  }
  return '';
}

// ---------------------------------------------------------------------------
// where the rule differs by package (v83 `countLine`)
// ---------------------------------------------------------------------------

/** Each package of the structure as the list counts it:
 *  'follow' | 'own' | 'adapted' | 'na'. */
export function packageKind(section, state, row) {
  const r = state && state.rows && state.rows[row.qwp_id];
  const kind = (r && r.kind) || 'follow';
  if (kind === 'auto') return forcedNull(section, row) ? 'na' : 'adapted';
  return kind;
}

export function groups(section, state, structure) {
  const out = {
    follow: [], own: [], adapted: [], na: [],
  };
  (structure || []).forEach((row) => { out[packageKind(section, state, row)].push(row); });
  return out;
}

export function countLine(section, state, structure) {
  const g = groups(section, state, structure);
  let s = `Common rule · ${plural(g.follow.length, 'QWP', 'QWPs')}`;
  if (g.own.length) {
    s += ` · ${g.own.length} with ${g.own.length === 1 ? 'a rule of its own' : 'rules of their own'} (${joinNames(g.own)})`;
  }
  if (g.adapted.length) s += ` · standard rule adapted to the parties of ${joinNames(g.adapted)}`;
  if (g.na.length) s += ` · not applicable in ${joinNames(g.na)}`;
  if (!g.own.length && !g.adapted.length && !g.na.length) s += ' · no exceptions';
  return s;
}

/** v83 `allNa`: the subarea exists in no package. */
export const allNotApplicable = (section, structure) => (structure || []).length > 0
  && (structure || []).every((row) => forcedNull(section, row));

export const notApplicableText = (section) => (section === 'rfi'
  ? 'Not applicable: every QWP is internal QA' : 'Not applicable: no QWP has three parties');
export const notApplicableHint = (section) => (section === 'rfi'
  ? 'Applies only to packages with more than one company'
  : 'Applies only to packages with an Executing, a Verification and an Acceptance Party');

/** The packages whose `needs_review` names this subarea, in structure order.
 *  `review` = `{qwp_id: [...]}` (`editor.status[section].review`). */
export const flaggedRows = (review, structure) => (structure || [])
  .filter((row) => Boolean(review && review[row.qwp_id] && review[row.qwp_id].length));

export const needsReviewText = (rows) => (rows.length ? `Needs review in ${joinNames(rows)}` : '');

/** v83 `row` button: "View rule" when the person can only look, "Review" when
 *  a package needs review, else "Edit". */
export function rowAction({ readOnly, flagged }) {
  if (readOnly) return 'View rule';
  return flagged ? 'Review' : 'Edit';
}

/** Everything a rule row draws (v83 `row`). */
export function ruleRow(section, state, structure, review, ctx = {}) {
  const allNa = allNotApplicable(section, structure);
  const flagged = flaggedRows(review, structure);
  return {
    value: allNa ? notApplicableText(section) : ruleSummary(section, state ? state.general : null, ctx),
    count: allNa ? notApplicableHint(section) : countLine(section, state, structure),
    flag: needsReviewText(flagged),
    action: rowAction({ readOnly: ctx.readOnly, flagged: flagged.length > 0 }),
  };
}

// ---------------------------------------------------------------------------
// step 4 — "Handover by Quality Work Package" (v83 `routeText`, `noticeText`)
// ---------------------------------------------------------------------------

export const NOT_ASSIGNED = 'Not assigned';

/** Who sits at a position of a package: its company, "Not assigned" for an
 *  Executing Party nobody is appointed to, null when the package has no such
 *  party. */
export function partyAt(row, position) {
  if (position === 'execution') {
    const e = row && row.executing;
    return e && e.appointed !== false && e.name ? e.name : NOT_ASSIGNED;
  }
  const p = row && row[position];
  return p ? (p.name || NOT_ASSIGNED) : null;
}

/** `[{text, missing}]` — the route a package's handover walks. */
export function routeParts(value, row) {
  const path = (value && value.path) || 'none';
  if (path === 'none') return [{ text: 'No handover — internal QA', missing: false }];
  return (PATH_POSITIONS[path] || []).map((pos) => {
    const who = partyAt(row, pos);
    return who ? { text: who, missing: false } : { text: `no ${POSITION_LABEL[pos]}`, missing: true };
  });
}

/** `[{n, text}]` — the notice of each handover (`n` null with one handover). */
export function noticeLines(value, options) {
  const path = (value && value.path) || 'none';
  if (path === 'none') return [{ n: null, text: 'Not applicable' }];
  const h = (value && value.handovers) || [];
  if (path === 'EVA') {
    return [0, 1].map((i) => ({ n: i + 1, text: handoverNoticeLabel(options, h[i] && h[i].notice) }));
  }
  return [{ n: null, text: handoverNoticeLabel(options, h[0] && h[0].notice) }];
}

/** "Handover lots": the package's asset groups (`asset_group_count`). */
export const lotsText = (n) => (typeof n === 'number' ? plural(n, 'asset group', 'asset groups') : '—');

/** One line of the table: route, source, notice, action. */
export function handoverLine(state, row, review, ctx = {}) {
  const value = state ? valueOf(state, row.qwp_id) : null;
  const flagged = Boolean(review && review[row.qwp_id] && review[row.qwp_id].length);
  const kind = packageKind('handover', state, row);
  let source = 'Common project rule';
  if (flagged) source = 'Needs review';
  else if (kind !== 'follow') source = 'Rule of its own';
  let action = flagged ? 'Review' : 'Edit';
  if (ctx.readOnly) action = 'View';
  return {
    route: routeParts(value, row),
    source,
    flagged,
    notice: noticeLines(value, ctx.options),
    action,
  };
}

/** v83 handover common row: "4 QWPs follow this rule · …". */
export const followLine = (state, structure) => `${plural(groups('handover', state, structure).follow.length,
  'QWP follows', 'QWPs follow')} this rule · new QWPs without an exception use it too`;

// ---------------------------------------------------------------------------
// the footer (v83 `footer`)
// ---------------------------------------------------------------------------

/** `revision` = the Revision in force (0 = none). */
export function footerNote({ inForce, revision, dirty }) {
  if (inForce) return `Revision ${revision} · view only`;
  if (dirty) return 'Unsaved changes';
  return `Draft saved · you can review everything before publishing${
    revision ? ` · Revision ${revision} stays in force` : ''}`;
}
